import { Redirect, router, useFocusEffect, usePathname } from 'expo-router';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { useCallback } from 'react';
import { KeyboardAvoidingView, Platform } from 'react-native';
import {
    CheckIcon,
    Icon,
    isWeb,
    SafeAreaView,
    ScrollView,
    Separator,
    tokens,
    useToastController,
    View,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { transformBillFromServer } from '../../../common/utils/billUtils';
import type { AccountSummary } from '../../../holistic/types/HolisticSummary';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../../logger/Logger';
import useUpdateBill, { type UpdateBillBody } from '../../hooks/useUpdateBill';
import {
    allBillsAtom,
    billsAccountsAtom,
    billsToReviewAtom,
    currentlyEditingBillFieldAtom,
    isEditingBillTransactionsAtom,
    selectedReviewBillAtom,
} from '../../state/BillsAtoms';
import {
    BillContext,
    BillCreationType,
    type IBill,
    type InlineEditableBillFields,
} from '../../types/Bill';
import { BillIcon, BillIconSize } from '../common/BillIcons';
import BillsHeader from '../common/BillsHeader';
import BillDetailsAttributes from '../details/BillDetailsAttributes';
import BillDetailsTransactions from '../details/BillDetailsTransactions';

const BillReviewDetailsNew = () => {
    LogRocketFacade.tagPage('BillReviewAddDetails');
    const toast = useToastController();
    const user = useAuthedUser();
    const pathname = usePathname();

    const [selectedBill, setSelectedBill] = useAtom(selectedReviewBillAtom);
    const { mutate: updateBill } = useUpdateBill();

    const billsAccounts = useAtomValue(billsAccountsAtom);
    const storeBillsToReview = useSetAtom(billsToReviewAtom);
    const updateAllBillsAtom = useSetAtom(allBillsAtom);
    const setCurrentlyEditingBillField = useSetAtom(
        currentlyEditingBillFieldAtom,
    );
    const setIsEditingBillTransactions = useSetAtom(
        isEditingBillTransactionsAtom,
    );

    // Determine if we're in Spinwheel context
    const isSpinwheelContext = pathname?.includes('/onboarding/expenses/');

    // leave bill/transaction editing state when closing modal
    useFocusEffect(
        useCallback(() => {
            return () => {
                setCurrentlyEditingBillField(null);
                setIsEditingBillTransactions(false);
            };
        }, []),
    );

    if (!selectedBill) {
        captureException(
            new Error('Attempted to load BillDetails with no selected Bill'),
        );
        const redirectPath = isSpinwheelContext
            ? '/onboarding/expenses/bills-review'
            : '/bills/bills-overview';
        return <Redirect href={redirectPath} />;
    }

    const submitBillUpdate = (
        name: keyof UpdateBillBody,
        value: string | number,
    ) => {
        // optimistically update UI with new bill, and fallback if the update fails
        const oldBill = selectedBill;
        const newBill = { ...selectedBill, [name]: value };
        setSelectedBill(newBill);

        const {
            id,
            accountId,
            displayName,
            payee,
            amount,
            cadence,
            timingLabel,
            timingDescription,
            category,
            lastPaidAt,
            memberMarkedAsSwitched,
        } = selectedBill as IBill;

        updateBill(
            {
                urlParams: {
                    id,
                    membershipId: user?.membership?.id ?? '',
                },
                body: {
                    accountId,
                    displayName,
                    payee,
                    amount,
                    cadence,
                    timingLabel,
                    timingDescription,
                    category,
                    lastPaidAt,
                    memberMarkedAsSwitched,
                    [name]: value,
                } satisfies UpdateBillBody,
            },
            {
                onSuccess: (updatedBillFromServer) => {
                    const transformedBill = transformBillFromServer(
                        updatedBillFromServer,
                    );
                    storeBillsToReview((prev) => {
                        return prev.map((pb) => {
                            return pb.id === transformedBill.id
                                ? transformedBill
                                : pb;
                        });
                    });
                    updateAllBillsAtom((prev) => {
                        return prev.map((pb) => {
                            return pb.id === transformedBill.id
                                ? transformedBill
                                : pb;
                        });
                    });
                    toast.show(`${payee} bill updated`, {
                        icon: (
                            <View
                                alignItems="center"
                                justifyContent="center"
                                backgroundColor="$green1000"
                                borderRadius={12}
                                padding={9}
                            >
                                <Icon
                                    icon={CheckIcon}
                                    size={24}
                                    color="$white"
                                />
                            </View>
                        ),
                    });
                    setSelectedBill(transformedBill);
                },
                onError: (error) => {
                    setSelectedBill(oldBill);
                    toast.show('Something went wrong');
                    captureException(error);
                    // TODO handle error UX
                },
            },
        );
    };

    const { icon, color, accountId, transactions } = selectedBill;

    const account = billsAccounts?.find(
        (a) => a.id === accountId,
    ) as AccountSummary;

    return (
        <>
            <SafeAreaView>
                <KeyboardAvoidingView
                    style={{
                        flex: 1,
                        height: '100%',
                        ...(isWeb && {
                            justifyContent: 'center',
                            alignItems: 'center',
                        }),
                    }}
                    behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                    keyboardVerticalOffset={90}
                >
                    <YStack
                        width="100%"
                        maxWidth={700}
                        paddingVertical={isWeb ? 24 : undefined}
                        flex={1}
                        paddingBottom={0}
                        justifyContent="space-between"
                        gap={16}
                    >
                        <BillsHeader
                            padding={24}
                            onBack={() => {
                                setSelectedBill(null);
                                setCurrentlyEditingBillField(null);
                                router.back();
                            }}
                            title="Details"
                        />
                        <ScrollView
                            showsVerticalScrollIndicator={isWeb}
                            paddingHorizontal={24}
                        >
                            <YStack
                                backgroundColor={tokens.color.light.brand.white}
                                height="100%"
                                separator={<Separator />}
                            >
                                <YStack gap={16} paddingTop={16}>
                                    <BillIcon
                                        color={color}
                                        icon={icon}
                                        size={BillIconSize.MEDIUM}
                                        hasShadow={true}
                                        borderRadius={20}
                                    />
                                    <BillDetailsAttributes
                                        bill={selectedBill}
                                        account={account}
                                        onInlineAttributePress={(
                                            fieldName: InlineEditableBillFields,
                                        ) => {
                                            setCurrentlyEditingBillField(
                                                fieldName,
                                            );
                                        }}
                                        onEdit={submitBillUpdate}
                                        context={
                                            isSpinwheelContext
                                                ? BillContext.ONBOARDING
                                                : BillContext.REVIEW
                                        }
                                    />
                                </YStack>
                                {selectedBill.creationType !==
                                    BillCreationType.MANUAL &&
                                    selectedBill.creationType !==
                                        BillCreationType.MONEY_MAPPER &&
                                    transactions && (
                                        <BillDetailsTransactions
                                            transactions={transactions ?? []}
                                            account={account}
                                            context={BillContext.REVIEW}
                                        />
                                    )}
                            </YStack>
                        </ScrollView>
                    </YStack>
                </KeyboardAvoidingView>
            </SafeAreaView>
        </>
    );
};

export default BillReviewDetailsNew;
